import { ScheduleTypes } from '../types/ISchedule';
import { FrequencyDetails, Interval, SelfServiceSchedule, WeekDayOccurrence } from '../types/SelfServiceSchedule';

/**
 * Helper class for validating self service schedules with frequencies
 * Based on ScheduleHelpers.java in the monolith
 * User docs example: extract refresh schedules on Cloud:
 * https://help.tableau.com/current/api/rest_api/en-us/REST/rest_api_ref.htm#create_cloud_extract_refresh_task
 */
export class SelfServiceScheduleValidator {
  /**
   * Validates a self service schedule request with frequencies and intervals
   * These errors should never be displayed to the user. 
   * 
   */
  public static validateRequest(schedule: SelfServiceSchedule) {
    if (!schedule.frequency || !schedule.frequencyDetails || !schedule.frequencyDetails.intervals) {
      throw new TypeError('Custom schedules must have a frequency and frequencyDetails and frequencyDetails.intervals');
    }

    const frequencyDetails = schedule.frequencyDetails;
    this.validateScheduleFrequencyDetailsTime(frequencyDetails.start);

    switch (schedule.frequency) {
      case ScheduleTypes.Hourly:
        this.validateHourlySchedule(frequencyDetails);
        return;
      case ScheduleTypes.Daily:
        this.validateDailySchedule(frequencyDetails);
        return;
      case ScheduleTypes.Weekly:
        this.validateWeeklySchedule(frequencyDetails);
        return;
      case ScheduleTypes.Monthly:
        this.validateMonthlySchedule(frequencyDetails);
        return;
    }
  }

  private static validateScheduleFrequencyDetailsTime(time: string) {
    if (!time) {
      throw new TypeError('FrequencyDetails time must have a value');
    }
    const timeUnits = time.split(':');
    if (
      time.length !== 8 ||
      timeUnits.length !== 3 ||
      timeUnits[0].length !== 2 ||
      timeUnits[1].length !== 2 ||
      timeUnits[2].length !== 2
    ) {
      throw new TypeError(
        'FrequencyDetails start or end must be in the format HH:MM:SS'
      );
    }

    const hours = parseInt(timeUnits[0]);
    const minutes = parseInt(timeUnits[1]);
    const seconds = parseInt(timeUnits[2]);

    const hoursIsNotValid = hours < 0 || hours > 24;
    const minutesIsNotValid = minutes < 0 || minutes > 55 || minutes % 5 !== 0;
    const secondsIsNotValid = seconds !== 0;

    if (hoursIsNotValid || minutesIsNotValid || secondsIsNotValid) {
      throw new TypeError(
        'Schedule FrequencyDetails start or end time value is not valid'
      );
    }
  }

  private static validateHourlySchedule(
    frequencyDetails: FrequencyDetails
  ) {
    this.validateScheduleFrequencyDetailsTime(frequencyDetails.end);

    frequencyDetails.intervals.interval.forEach((interval: Interval) => {
      if (
        (interval.hours && interval.hours !== 1) ||
        (interval.minutes && interval.minutes !== 60)
      ) {
        throw new TypeError(
          'The minute intervals must be 60 mins and hour intervals must be 1 hr for an hourly schedule'
        );
      }
    });

    this.validateStartAndEndMinutesAreEqual(
      frequencyDetails.start,
      frequencyDetails.end
    );
  }

  private static validateDailySchedule(
    frequencyDetails: FrequencyDetails
  ) {
    const hourIntervals = frequencyDetails.intervals.interval.filter(
      (interval: Interval) => interval.hours
    );
    if (hourIntervals.length === 0 && frequencyDetails.end) {
      throw new TypeError('End time should never be provided if there are no hour intervals');
    }
    if (hourIntervals.length > 1) {
      throw new TypeError('Daily schedule must have at most one hour interval');
    }
    this.validateScheduleFrequencyDetailsTime(frequencyDetails.end);
    this.validateStartAndEndMinutesAreEqual(
      frequencyDetails.start,
      frequencyDetails.end
    );
  }

  private static validateStartAndEndMinutesAreEqual(
    start: string,
    end: string
  ) {
    const startMinutes = parseInt(start.split(':')[1]);
    const endMinutes = parseInt(end.split(':')[1]);
    if (startMinutes !== endMinutes) {
      throw new TypeError(
        'The start and end time must have a difference divisible by 60 minutes'
      );
    }
  }

  private static validateWeeklySchedule(
    frequencyDetails: FrequencyDetails
  ) {
    if (frequencyDetails.end) {
      throw new TypeError(
        'End time should never be provided for a weekly schedule'
      );
    }

    const intervals = frequencyDetails.intervals.interval;
    const dayIntervals = intervals.filter((interval: Interval) => interval.weekDay);
    if (dayIntervals.length !== 1) {
      throw new TypeError(
        'There must be exactly one weekDay interval for a weekly schedule'
      );
    }
  }

  private static validateMonthlySchedule(
    frequencyDetails: FrequencyDetails
  ) {
    if (frequencyDetails.end) {
      throw new TypeError(
        'End time should never be provided for a monthly Schedule'
      );
    }

    const intervals = frequencyDetails.intervals.interval;
    const firstInterval = intervals[0];
    const firstMonth = firstInterval.monthDay;
    if (!firstMonth) {
      throw new TypeError(
        'The first interval for a monthly schedule must be a month interval'
      );
    }
    if (
      firstMonth === WeekDayOccurrence.LastDay &&
      !firstInterval.weekDay
    ) {
      // indicates the last day of the month, nothing else to check
      return;
    }
    if (this.isOccurrence(firstMonth)) {
      if (!firstInterval.weekDay)
        throw new TypeError(
          'A month interval with a weekDay occurrence must have a weekDay for a monthly schedule'
        );
      return;
    }

    // The first month interval is not an occurrence so it must be a day
    const monthIntervals = intervals.filter((interval: Interval) => interval.monthDay);
    monthIntervals.forEach((interval: Interval) => {
      const intervalMonth = interval.monthDay;
      if (this.isOccurrence(intervalMonth)) {
        throw new TypeError(
          'If first month interval is a day, the remaining month intervals must be days for a monthy schedule'
        );
      }
      if (intervalMonth < 1 || intervalMonth > 31) {
        throw new TypeError('Month interval is invalid');
      }
    });
  }

  private static isOccurrence(monthDay: WeekDayOccurrence | number) {
    return typeof monthDay === 'string';
  }
}
